import {
  Avatar,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover,
  TextField,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import { ChangeEvent, KeyboardEvent, useState } from "react";
import { Client } from "typesense";
import { SearchResponse } from "typesense/lib/Typesense/Documents";
import Iconify from "~src/components/Iconify";
import { PATH_BRANDS } from "~src/routes/paths";

const SEARCH_KEY = import.meta.env.VITE_SEARCH_KEY || "";
const SEARCH_NODE = import.meta.env.VITE_SEARCH_NODE || "";

const client = new Client({
  nodes: [
    {
      host: SEARCH_NODE,
      port: 443,
      protocol: "https",
    },
  ],
  apiKey: SEARCH_KEY,
  cacheSearchResultsForSeconds: 2 * 60,
});

interface SearchBarProps {
  onSearch: (searchText: string) => void;
}

type BrandResult = {
  id: string;
  brand_name: string;
  description?: string;
  image?: string;
};

export const SearchBar: React.FC<SearchBarProps> = ({ onSearch }) => {
  const [searchText, setSearchText] = useState("");
  const [showClearButton, setShowClearButton] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLInputElement | null>(null);

  const [autocompleteResults, setAutocompleteResults] =
    useState<SearchResponse<BrandResult>>();

  const handleSearch = () => {
    if (searchText.length >= 4) {
      onSearch(searchText);
      setShowClearButton(false);
      // Close popover and clear search after submitting
      setAnchorEl(null);
      setSearchText("");
      setAutocompleteResults(undefined);
    }
  };

  const handleClear = () => {
    setSearchText("");
    setShowClearButton(false);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchText(value);

    if (value.length < 2) {
      setShowClearButton(false);
      setAutocompleteResults(undefined);
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
      autocomplete(value);
    }
  };

  const autocomplete = async (searchText: string) => {
    const results = await client
      .collections<BrandResult>("brands")
      .documents()
      .search({
        q: searchText,
        query_by: ["brand_name", "brand_url"],
      });
    setAutocompleteResults(results);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <>
      <TextField
        placeholder="Search"
        fullWidth
        value={searchText}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        style={{ height: 40 }}
        slotProps={{
          input: {
            style: { height: 40 },
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="iconamoon:search" />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {showClearButton && (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClear}>
                      <Iconify icon="uil:times-circle" />
                    </IconButton>
                  </InputAdornment>
                )}
              </>
            ),
          }
        }}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        disableAutoFocus={true}
        disableEnforceFocus={true}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <List>
          {autocompleteResults?.hits?.map((result) => (
            <ListItem
              sx={{
                cursor: "pointer",
                color: "inherit",
                textDecoration: "none",
              }}
              component={RouterLink}
              to={PATH_BRANDS.overview(parseInt(result.document.id, 10))}
              key={result.document.id}
              onClick={() => {
                setAnchorEl(null);
                setSearchText("");
                setAutocompleteResults(undefined);
              }}
            >
              <ListItemAvatar>
                <Avatar src={result.document.image} />
              </ListItemAvatar>
              <ListItemText primary={result.document.brand_name} />
            </ListItem>
          ))}
        </List>
      </Popover>
    </>
  );
};
